// pre-render bootstrap: fetch per-portal config and call initPortalConfig before src/index.js dynamic-imports the React tree

import { API, DEBUG, BASEURL, IS_DEV } from '../constants'
import { PORTAL_IDENTIFIER } from '../env'
import { checkFile } from '../components/checkFile'
import { initPortalConfig, CAMERAS, ROUTES, TITLE, REFRESH_MIN, ORDER_ID, DEFAULT_LANG, PROJECT_FILE_DATE, PROJECT_FINISHED } from '../api/portalConfig'

const FETCH_TIMEOUT_MS = 10000 // hard cap so a hung backend surfaces the error screen instead of leaving the spinner forever
const ABSOLUTE_URL = /^https?:\/\//i

async function loadPortalConfig() {
    const id = PORTAL_IDENTIFIER
    if (!id) throw new Error('No portal identifier could be resolved from hostname or env.js DEV_DEFAULT_HOST')

    const legacyConfig = loadLegacyConfig() // parallel with the graphql fetch; never rejects - resolves before transformOrder reads window.conf
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(new Error(`fetch portal identifier "${id}" timeout after ${FETCH_TIMEOUT_MS}ms`)), FETCH_TIMEOUT_MS)

    let res
    try {
        res = await fetch(API, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                query:  `query PortalConfig($id: String!) {
                    ordersQuery {
                        portals(where: { url: { eq: $id } }) {
                            feature {
                                order {
                                    id
                                    projectLanguage
                                    features(where: { type: { in: [0, 2, 3, 4, 8] } }) {
                                        type
                                        featureDetail { id cameraIdx archived intervall uri playerUrl }
                                        portal { title picture picturePassword livestream livestreamPassword archiv archivePassword timelapse timelapsePassword mySite mySitePassword access accessPassword projectFileDate }
                                    }
                                    orderItems { index camera { cameraPortalName } }
                                }
                            }
                        }
                    }
                }`,
                variables: { id }
            }),
            signal: controller.signal
        })
    } finally {
        clearTimeout(timeoutId)
    }

    if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`)

    const body = await res.json()
    if (body.errors?.length) throw new Error(`GraphQL: ${body.errors[0].message}`)

    const portal = body.data?.ordersQuery?.portals?.[0]
    if (!portal) throw new Error(`No portal found for identifier "${id}"`)

    const order = portal.feature?.order
    if (!order) throw new Error(`Portal "${id}" has no order`)

    await legacyConfig // window.conf (legacy LAYOUT override + dev cameras) must be ready before transformOrder reads it
    initPortalConfig(transformOrder(order))

    DEBUG && console.log('[PortalConfigLoader] Initialized', id, { ORDER_ID, TITLE, REFRESH_MIN, DEFAULT_LANG, PROJECT_FILE_DATE, PROJECT_FINISHED, ROUTES, CAMERAS })
}

// optional per-deploy config.js (old projects): sets window.conf with the legacy PROJECT_FILE_DATE/PROJECT_FINISHED override (read in transformOrder) + dev cameras
async function loadLegacyConfig() {
    try {
        const url = new URL('config.js', document.baseURI).href // same-origin, document-relative (mirrors the old ./config.js script src)
        const { exists, contentType } = await checkFile(url)
        if (!exists || contentType === 'html') return // 404 or SPA fallback - skip; never inject html as a script
        await new Promise(resolve => {
            const el = document.createElement('script')
            el.src = url // external src (not inline) - CSP-safe under script-src 'self'
            el.onload = resolve
            el.onerror = resolve // load failure - proceed without the override
            document.head.appendChild(el)
        })
    } catch (err) {
        DEBUG && console.warn('[PortalConfigLoader] legacy config.js skipped:', err)
    }
}

// transform db values like "Deutsch:de"; default de
function transformLang(lang) {
    const s = String(lang ?? '')
    if (/niederl/i.test(s)) return 'nl'
    if (/englisch/i.test(s)) return 'en'
    if (/deutsch/i.test(s)) return 'de'
    return 'de'
}

// camera simulation (dev-only)
function applyDevCameras(cameras) {
    if (!IS_DEV) return cameras
    const override = (typeof window !== 'undefined') ? window.conf?.DEV_CAMERAS : undefined
    if (!override) return cameras
    try {
        const next = typeof override === 'function' ? override(cameras.map(c => ({ ...c }))) : override
        if (!Array.isArray(next)) {
            console.warn('[PortalConfigLoader] DEV_CAMERAS ignored - expected an array or (cameras) => array')
            return cameras
        }
        console.warn(`[PortalConfigLoader] DEV_CAMERAS override active - ${next.length} simulated camera(s)`)
        return next
    } catch (err) {
        console.warn('[PortalConfigLoader] DEV_CAMERAS threw - using fetched cameras:', err)
        return cameras
    }
}

function transformOrder(order) {
    const features = order.features ?? []
    const byType = {
        0: features.find(f => f.type === 0),
        2: features.find(f => f.type === 2),
        3: features.find(f => f.type === 3),
        4: features.find(f => f.type === 4),
        8: features.find(f => f.type === 8)
    }

    // cameraIdx is 1-based throughout the response
    const detailsByIdx = typeFeature => {
        const m = new Map()
        for (const d of (typeFeature?.featureDetail ?? [])) m.set(d.cameraIdx, d)
        return m
    }
    const t0 = detailsByIdx(byType[0])
    const t3 = detailsByIdx(byType[3])
    const t4 = detailsByIdx(byType[4])
    const t8 = detailsByIdx(byType[8])

    // orderItems.index ↔ cameraIdx; normalise "---" placeholder to empty string
    const nameByIdx = new Map()
    for (const item of (order.orderItems ?? [])) {
        const raw = (item?.camera?.cameraPortalName ?? '').trim()
        nameByIdx.set(item.index, raw === '---' ? '' : raw)
    }

    const base = BASEURL.replace(/\/+$/, '') + '/'
    // join() prefixes BASEURL only for relative paths; absolute URLs (https://…) pass through so external CDN/livestream URIs aren't mangled
    const join = relOrAbs => {
        const s = String(relOrAbs ?? '')
        return ABSOLUTE_URL.test(s) ? s : base + s.replace(/^\/+/, '')
    }

    // type 3 (archive) is the canonical camera source
    const camIdxs = Array.from(t3.keys()).sort((a, b) => a - b)

    // warn if cameraIdx has gaps - consumers that index CAMERAS[id] assume id == array index (which only holds when cameraIdx is consecutive starting from 1)
    const hasGaps = camIdxs.some((idx, i) => idx !== i + 1)
    if (hasGaps) console.warn('[PortalConfigLoader] cameraIdx values are not consecutive starting from 1:', camIdxs, '— CAMERAS[id] lookups may misbehave')

    const cameras = camIdxs.map(idx => {
        const arch = t3.get(idx)
        const img = t0.get(idx)
        const tl = t4.get(idx)
        const lv = t8.get(idx)
        return {
            id: idx - 1,
            name: nameByIdx.get(idx) ?? '',
            image: (img && img.archived === 0 && img.uri) ? join(img.uri) : '',
            video: (lv && lv.archived === 0 && lv.uri) ? lv.uri : '', // type 8 uri is absolute
            timelapse: (tl && tl.archived === 0 && tl.uri) ? join(tl.uri) : '',
            download: arch?.playerUrl ? join(arch.playerUrl) : '',
            featureDetailId: arch?.id ?? 0
        }
    })

    // first positive intervall across type-3 cameras; 0/missing means "fault" - fall back to 10min so refresh stays active
    const refreshFromType3 = camIdxs
        .map(idx => t3.get(idx)?.intervall)
        .find(i => Number.isFinite(i) && i > 0)

    const portalInfo = byType[2]?.portal ?? {}

    // old projects carry PROJECT_FILE_DATE/PROJECT_FINISHED in config.js (set by Powertool) - prefer whichever source is truthy, db first
    const dbFileDate = String(portalInfo.projectFileDate ?? '').trim()
    const dbFinished = dbFileDate !== '' && !dbFileDate.includes('---')
    const cfgLayout = (typeof window !== 'undefined' && window.conf?.LAYOUT) || {}
    const projectFileDate = (dbFinished ? dbFileDate : '') || cfgLayout.PROJECT_FILE_DATE || ''
    const projectFinished = dbFinished || !!cfgLayout.PROJECT_FINISHED

    return {
        cameras: applyDevCameras(cameras),
        title: portalInfo.title || '',
        refreshMin: refreshFromType3 ?? 10,
        orderId: order.id,
        defaultLang: transformLang(order.projectLanguage),
        projectFileDate,
        projectFinished,
        protection: {
            HOME: !!portalInfo.picturePassword,
            LIVE: !!portalInfo.livestreamPassword,
            TIMELAPSE: !!portalInfo.timelapsePassword,
            ARCHIVE: !!portalInfo.archivePassword,
            MYSITE: !!portalInfo.mySitePassword,
            ACCESS: !!portalInfo.accessPassword
        },
        disabled: {
            HOME: !portalInfo.picture,
            LIVE: !portalInfo.livestream,
            ARCHIVE: !portalInfo.archiv,
            TIMELAPSE: !portalInfo.timelapse,
            MYSITE: !portalInfo.mySite, // gates only the my-site core accordions - /mysite tab stays reachable (see Menu)
            ACCESS: !portalInfo.access,
        }
    }
}

const ERROR_MESSAGES = {
    de: {
        sorry: 'SORRY',
        configBody: 'Die Portal-Konfiguration konnte nicht geladen werden.',
        appBody: 'Die Anwendung konnte nicht geladen werden.',
        reload: 'Neu laden'
    },
    en: {
        sorry: 'SORRY',
        configBody: 'Portal configuration could not be loaded.',
        appBody: 'Application could not be loaded.',
        reload: 'Reload'
    },
    nl: {
        sorry: 'SORRY',
        configBody: 'Portaalconfiguratie kon niet worden geladen.',
        appBody: 'Applicatie kon niet worden geladen.',
        reload: 'Herladen'
    }
}

function resolveLang() {
    const browserLang = (navigator.language || '').slice(0, 2).toLowerCase()
    return ERROR_MESSAGES[browserLang] ? browserLang : 'de'
}

function renderErrorScreen({ sorry, body, reload }) {
    const root = document.getElementById('root')
    if (!root) return

    root.innerHTML = `
        <style>
            .btv-error { position:fixed; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; background:#000; color:#fff; font-family:"Roboto","Helvetica","Arial",sans-serif; padding:16px; box-sizing:border-box; }
            .btv-error h1 { margin:0; font-size:6rem; font-weight:300; line-height:1.167; letter-spacing:-0.01562em; user-select:none; }
            .btv-error h6 { margin:0; font-size:1.25rem; font-weight:500; line-height:1.6; letter-spacing:0.0075em; max-width:80%; text-align:center; user-select:none; }
            .btv-error button { display:inline-flex; align-items:center; justify-content:center; margin-top:20px; min-width:64px; padding:8px 22px; border:0; border-radius:4px; background:#003366; color:#fff; font-family:inherit; font-size:0.9375rem; font-weight:500; line-height:1.75; letter-spacing:0.02857em; text-transform:uppercase; cursor:pointer; user-select:none; box-sizing:border-box; box-shadow:0 3px 1px -2px rgba(0,0,0,0.2),0 2px 2px 0 rgba(0,0,0,0.14),0 1px 5px 0 rgba(0,0,0,0.12); transition:background-color 250ms cubic-bezier(0.4,0,0.2,1),box-shadow 250ms cubic-bezier(0.4,0,0.2,1); }
            .btv-error button:hover { background:#002447; box-shadow:0 2px 4px -1px rgba(0,0,0,0.2),0 4px 5px 0 rgba(0,0,0,0.14),0 1px 10px 0 rgba(0,0,0,0.12); }
            .btv-error button svg { width:20px; height:20px; margin-right:8px; margin-left:-4px; fill:currentColor; }
        </style>
        <div class="btv-error">
            <h1>${sorry}</h1>
            <h6>${body}</h6>
            <button id="btv-config-reload" type="button">
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg>${reload}
            </button>
        </div>
    `
    document.getElementById('btv-config-reload')?.addEventListener('click', () => {
        try { sessionStorage.removeItem('btv_chunk_retry') } catch {} // clear chunk-retry guard so a manual reload always re-attempts the recovery path
        window.location.reload()
    })
}

function showConfigErrorScreen(err) {
    console.error('[PortalConfigLoader] Failed to load:', err)
    const msg = ERROR_MESSAGES[resolveLang()]
    renderErrorScreen({ sorry: msg.sorry, body: msg.configBody, reload: msg.reload })
}

// shown when the React tree (or any post-config code) fails to load/render after a successful config fetch
function showAppErrorScreen(err) {
    console.error('[PortalConfigLoader] Failed to start:', err)
    const msg = ERROR_MESSAGES[resolveLang()]
    renderErrorScreen({ sorry: msg.sorry, body: msg.appBody, reload: msg.reload })
}

// detect webpack/CRA chunk-load failures - stale index.html after a deploy referencing hashes the server no longer has
function isChunkLoadError(err) {
    if (!err) return false
    if (err.name === 'ChunkLoadError') return true
    return /Loading chunk |ChunkLoadError|Failed to fetch dynamically imported module/i.test(err.message || '')
}

export { loadPortalConfig, showConfigErrorScreen, showAppErrorScreen, isChunkLoadError }